import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../assets/css/request-trail.css";
import BackgroundDecor from "../components/RequestTrail/BackgroundDecor";
import ShapeIcon from "../shape.svg";
const FEATURES = [
  { icon: ShapeIcon, label: "Employee Management" },
  { icon: ShapeIcon, label: "Payroll Automation" },
  { icon: ShapeIcon, label: "Attendance & Leave Tracking" },
  { icon: ShapeIcon, label: "Custom Notifications" },
  { icon: ShapeIcon, label: "Real-Time Dynamic Dashboard" },
];

const EMPLOYEE_RANGES = ["1-10", "11-50", "51-200", "201-500", "500+"];

const trialSchema = Yup.object().shape({
  fullName: Yup.string().min(2, "Too short").max(70, "Too long").required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  company: Yup.string().max(100, "Too long"),
  employeeCount: Yup.string().required("Required"),
  phone: Yup.string().required("Required"),
  message: Yup.string().max(500, "Too long"),
});

const RequestTrail = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fullNameRef = useRef(null);
  const emailRef = useRef(null);
  const companyRef = useRef(null);
  const employeeRef = useRef(null);
  const phoneRef = useRef(null);
  const messageRef = useRef(null);

  const [activeLabelFullName, setActiveLabelFullName] = useState(false);
  const [activeLabelEmail, setActiveLabelEmail] = useState(false);
  const [activeLabelCompany, setActiveLabelCompany] = useState(false);
  const [activeLabelEmployee, setActiveLabelEmployee] = useState(false);
  const [activeLabelPhone, setActiveLabelPhone] = useState(false);
  const [activeLabelMessage, setActiveLabelMessage] = useState(false);

  const handleInputFocus = (setter) => setter(true);

  const handleInputBlur = (e, setter, value) => {
    handleBlur(e);
    setter(!!value);
  };
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      company: "",
      employeeCount: "",
      phone: "",
      message: "",
    },
    validationSchema: trialSchema,
    onSubmit: async (vals, { setErrors }) => {
      setLoading(true);
      try {
        const trailAPIBaseUrl = process.env.REACT_APP_TRAIL_REQUEST_BASE_URL;
        const res = await fetch(trailAPIBaseUrl+"/api/request-trial.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(vals),
        });
        if (!res.ok) {
          const body = await res.text();
          let message = body;
          try {
            const json = JSON.parse(body);
            const fieldErrors = json.errors && typeof json.errors === "object" ? json.errors : null;
            if (fieldErrors && Object.keys(fieldErrors).length) {
              // Show server field errors under their inputs, and list them in the toast
              setErrors(fieldErrors);
              message = Object.values(fieldErrors).join(", ");
            } else {
              message = json.message || json.error || body;
            }
          } catch {}
          throw new Error(message || `Request failed (${res.status} ${res.statusText})`);
        }
        resetForm();
        setSubmitted(true);
        window.scrollTo(0, 0);
      } catch (err) {
        console.error("Request trial submission failed:", err);
        toast.error(err.message || "Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const featureItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '4px 0',
  };

  const featureTextStyle = {
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '140%',
    letterSpacing: '0%',
    verticalAlign: 'middle',
    color: '#475569',
    margin: 0,
  };

  const iconStyle = {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  if (submitted) {
    return (
      <div className="trial-thank-you">
        <h1>Thank you!</h1>
        <p>A SmartOffice specialist will contact you soon.</p>
        <Link to="/" className="btn-go-home">
          Go to home
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </div>
    );
  }

  return (
    <div className="contact-page-wrapper">
      <BackgroundDecor />

      <div className="container py-5 position-relative">
        <ToastContainer />
        <div className="row align-items-center trial-row g-4">
          {/* Left column */}
          <div className="col-12 col-lg-6 trial-left">
            <h1>Simplify HR & Payroll</h1>
            <p className="lead-text" style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '140%',
              letterSpacing: '0%',
              verticalAlign: 'middle',
              color: '#475569',
              maxWidth: '543px',
              height: '44px',
              margin: '0 0 20px 0',
            }}>
              One platform to manage employees, payroll, attendance, leave, and insights.
            </p>

            <p className="feature-heading">Feature Highlights:</p>
            <ul className="feature-list list-unstyled">
              {FEATURES.map((f) => (
                <li key={f.label} className="feature-item d-flex align-items-center gap-2" style={featureItemStyle}>
                  <i style={iconStyle}><img src={ShapeIcon}></img></i>
                  <span style={featureTextStyle}>{f.label}</span>
                </li>
              ))}
            </ul>

            <p className="cta-line">Fill out the form to start your free trial</p>
          </div>

          {/* Right column - form card, field styling matches ModalAddEdit spec */}
          <div className="col-12 col-lg-6">
            <div className="form-card mx-auto">
              <div className="form-header text-center">
                <h2>Access Full Version for 30 Days!</h2>
                <p>
                  We're happy to walk you through SmartOffice. Share a few details below
                  and one of our specialists will reach out to set up your free trial.
                </p>
                <div className="trial-badge">Book Your Free Trial</div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className={activeLabelFullName ? "active" : "deactive"}>
                    Full name
                  </label>

                  <input
                    ref={fullNameRef}
                    type="text"
                    name="fullName"
                    className="form-control"
                    value={values.fullName}
                    onChange={handleChange}
                    onFocus={() => handleInputFocus(setActiveLabelFullName)}
                    onBlur={(e) =>
                      handleInputBlur(e, setActiveLabelFullName, values.fullName)
                    }
                  />

                  {errors.fullName && touched.fullName && (
                    <p className="form-error">{errors.fullName}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className={activeLabelEmail ? "active" : "deactive"}>
                    Email address
                  </label>

                  <input
                    ref={emailRef}
                    type="email"
                    name="email"
                    className="form-control"
                    autoComplete="off"
                    value={values.email}
                    onChange={handleChange}
                    onFocus={() => handleInputFocus(setActiveLabelEmail)}
                    onBlur={(e) =>
                      handleInputBlur(e, setActiveLabelEmail, values.email)
                    }
                  />

                  {errors.email && touched.email && (
                    <p className="form-error">{errors.email}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className={activeLabelCompany ? "active" : "deactive"}>
                    Company name
                  </label>

                  <input
                    ref={companyRef}
                    type="text"
                    name="company"
                    className="form-control"
                    value={values.company}
                    onChange={handleChange}
                    onFocus={() => handleInputFocus(setActiveLabelCompany)}
                    onBlur={(e) =>
                      handleInputBlur(e, setActiveLabelCompany, values.company)
                    }
                  />

                  {errors.company && touched.company && (
                    <p className="form-error">{errors.company}</p>
                  )}
                </div>

                <div className="row">
                  <div className="col-lg-6 col-md-6">
                    <div className="form-group">
                      <select
                        ref={employeeRef}
                        name="employeeCount"
                        className="form-control"
                        value={values.employeeCount}
                        onChange={handleChange}
                        onFocus={() => handleInputFocus(setActiveLabelEmployee)}
                        onBlur={(e) =>
                          handleInputBlur(
                            e,
                            setActiveLabelEmployee,
                            values.employeeCount
                          )
                        }
                      >
                        <option value="">Number of employee</option>

                        {EMPLOYEE_RANGES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>

                      {errors.employeeCount && touched.employeeCount && (
                        <p className="form-error">{errors.employeeCount}</p>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-6">
                    <div className="form-group phone-group">
                      <label className={activeLabelPhone ? "active" : "deactive"}>
                        Phone Number
                      </label>

                      <PhoneInput
                        ref={phoneRef}
                        className="form-control"
                        defaultCountry="BD"
                        international
                        value={values.phone}
                        onChange={(phone) => {
                          setFieldValue("phone", phone);
                          setActiveLabelPhone(!!phone);
                        }}
                        onFocus={() => handleInputFocus(setActiveLabelPhone)}
                        onBlur={() => setActiveLabelPhone(!!values.phone)}
                      />

                      {errors.phone && touched.phone && (
                        <p className="form-error">{errors.phone}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className={activeLabelMessage ? "active" : "deactive"}>
                    Your Message (optional)
                  </label>

                  <textarea
                    ref={messageRef}
                    name="message"
                    rows={3}
                    className="form-control"
                    value={values.message}
                    onChange={handleChange}
                    onFocus={() => handleInputFocus(setActiveLabelMessage)}
                    onBlur={(e) =>
                      handleInputBlur(
                        e,
                        setActiveLabelMessage,
                        values.message
                      )
                    }
                  />

                  {errors.message && touched.message && (
                    <p className="form-error">{errors.message}</p>
                  )}
                </div>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 btn-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>

                <div className="form-footer text-center">
                  <p style={{
                      fontFamily: 'Public Sans, sans-serif',
                      fontWeight: 400,
                      fontSize: '10px',
                      lineHeight: '100%',
                      letterSpacing: '0px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      textDecorationStyle: 'solid',
                      textDecorationOffset: '0%',
                      textDecorationThickness: '0%',
                      color: '#919EAB',
                    }}>
                    By submitting this form, you agree to receive marketing communications
                    from SmartOffice. See our <a href="/privacy-policy" style={{
                      fontFamily: 'Public Sans, sans-serif',
                      fontWeight: 400,
                      fontSize: '10px',
                      lineHeight: '100%',
                      letterSpacing: '0px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      textDecoration: 'underline',
                      textDecorationStyle: 'solid',
                      textDecorationOffset: '0%',
                      textDecorationThickness: '0%',
                      color: '#919EAB',
                      cursor: 'pointer',
                    }}>Privacy Policy</a>.
                    You can opt out at any time.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestTrail;